import { ActionCards } from "@/components/home/ActionCards";
import { CharityTrekBand } from "@/components/home/CharityTrekBand";
import { Hero } from "@/components/home/Hero";
import { WhyHiscus } from "@/components/home/WhyHiscus";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import {
  LocationCards,
  chosenFocalPoint,
  linkSlot,
  visibleItems,
  visibleLinks,
} from "@/components/cms/SectionRenderer";
import { ArrowRight, ButtonLink, TextLink } from "@/components/ui/Button";
import { PillarCard, StoryCard } from "@/components/ui/Cards";
import { ImpactCounter } from "@/components/ui/ImpactCounter";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { MediaImage } from "@/components/ui/MediaImage";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useContent, useMediaByKey, usePillars, useSettings } from "@/content-context";
import type { MediaItem } from "@/data/media";
import { womenGirls } from "@/data/media";
import { org } from "@/data/site";
import { blurIn, imageReveal } from "@/lib/motion";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

/**
 * The homepage, as the sections an editor arranged.
 *
 * Each section below keeps the layout it was designed with; what changed is who
 * decides the order. The page editor lists these in the order they appear here,
 * and moving one there moves it on the site. A section somebody adds in the
 * panel renders through the generic renderer, after or between these.
 */

export const loader = pageLoader("home");

export const meta = () =>
  buildMetadata({
    title: `${org.legalName} | Community-led change in Nepal since 1998`,
    description:
      "HISCUS Nepal works alongside women, children and local communities to expand learning, wellbeing, livelihoods and sustainable recovery. Registered nonprofit, established 1998.",
    path: "/",
    titleIsComplete: true,
  });

function HeroSection({ section }: SectionProps) {
  return <Hero section={section} />;
}

function WhySection({ section }: SectionProps) {
  return <WhyHiscus section={section} />;
}

/*
  Evidence band. The reference sites all pair a positioning claim with hard
  numbers here; Mercy Corps uses "95% of team members are from the countries
  where they work" as its differentiator. HISCUS's equivalent is simply true
  and stronger: it is a Nepali organisation, not a country office.
*/
function EvidenceSection({ section }: SectionProps) {
  const { impact } = useContent();
  const links = visibleLinks(section.ctas);
  return (
    <Section tone="ivory" ariaLabelledBy="record-heading">
      <Reveal>
        {section.eyebrow ? <p className="eyebrow">{section.eyebrow}</p> : null}
        <h2 id="record-heading" className="mt-4 max-w-4xl" style={{ fontSize: "var(--step-h2)" }}>
          {section.title || "A small number of places, worked in repeatedly, with something durable left behind each time."}
        </h2>
      </Reveal>

      <RevealGroup as="ul" className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
        {impact.map((metric) => (
          <Reveal as="li" key={metric.key} variants={blurIn}>
            <ImpactCounter
              value={metric.value}
              suffix={metric.suffix ?? undefined}
              label={metric.label}
              context={metric.context}
              asOf={metric.asOf}
            />
          </Reveal>
        ))}
      </RevealGroup>

      {links.length ? (
        <Reveal className="mt-11 flex flex-wrap gap-x-10 gap-y-4">
          {links.map((cta) => (
            <TextLink key={cta.href + cta.label} href={cta.href}>
              {cta.label}
            </TextLink>
          ))}
        </Reveal>
      ) : null}
    </Section>
  );
}

/* The four programme pillars, from the Programmes collection. */
function ProgrammesSection({ section }: SectionProps) {
  const pillars = usePillars();
  const all = linkSlot(section, 0);
  return (
    <Section ariaLabelledBy="pillars-heading">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading id="pillars-heading" eyebrow={section.eyebrow} title={section.title || "Four programmes"} />
        {all ? <TextLink href={all.href}>{all.label}</TextLink> : null}
      </div>

      <RevealGroup as="ul" className="mt-11 grid gap-7 lg:grid-cols-2">
        {pillars.map((pillar) => (
          <Reveal as="li" key={pillar.slug}>
            <PillarCard pillar={pillar} />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/* The places HISCUS works in, directly beneath what it does there. */
function WhereWeWorkSection({ section }: SectionProps) {
  return <LocationCards section={section} />;
}

/* Three ways in, replacing a six-row audience matrix. */
function GetInvolvedSection({ section }: SectionProps) {
  const actions = visibleItems(section).map((item) => ({
    title: item.title ?? "",
    who: item.eyebrow ?? "",
    body: item.text ?? "",
    href: item.href || "/contact",
    cta: item.label || "Get in touch",
  }));
  return (
    <Section tone="mist" ariaLabelledBy="audience-heading">
      <SectionHeading
        id="audience-heading"
        eyebrow={section.eyebrow}
        title={section.title || "Three ways to take part"}
        lead={section.lead}
      />
      {actions.length ? (
        <div className="mt-10">
          <ActionCards actions={actions} />
        </div>
      ) : null}
    </Section>
  );
}

/* A documented initiative, with the dated facts of the project it names. */
function FeaturedSection({ section }: SectionProps) {
  const { projects } = useContent();
  const byKey = useMediaByKey();
  const project = projects.find((candidate) => candidate.slug === (section.refSlug || "empowering-with-dignity"));
  const title = section.title || project?.title;
  if (!title) return null;

  const image = byKey(section.mediaKey) ?? womenGirls[1];
  const read = linkSlot(section, 0);

  return (
    <Section ariaLabelledBy="featured-heading">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal variants={imageReveal}>
          <MediaFigure
            item={image}
            ratio="4 / 3"
            sizes="(min-width: 1024px) 50vw, 100vw"
            showCaption={false}
            radius={24}
            objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
          />
        </Reveal>

        <Reveal>
          {/*
            "Documented initiative" until 2026, above facts dated 2025. The
            organisation has published nothing from 2026 yet, so the label
            now says what the evidence actually supports. It becomes
            "Current initiative" again the moment a dated 2026 session is
            confirmed, not before.
          */}
          {section.eyebrow ? <p className="eyebrow">{section.eyebrow}</p> : null}
          <h2 id="featured-heading" className="mt-4" style={{ fontSize: "var(--step-h2)" }}>
            {title}
          </h2>
          {section.lead || project?.summary ? (
            <p className="mt-5 text-muted" style={{ fontSize: "var(--step-lead)" }}>
              {section.lead || project?.summary}
            </p>
          ) : null}

          {project?.facts.length ? (
            <dl className="mt-9 space-y-4">
              {project.facts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1 border-b border-line-strong/60 pb-4"
                >
                  <dt className="text-[0.9375rem] text-muted">{fact.label}</dt>
                  <dd className="font-serif text-[1.25rem] text-midnight">{fact.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {read ? (
            <div className="mt-8">
              <TextLink href={read.href}>{read.label}</TextLink>
            </div>
          ) : null}
        </Reveal>
      </div>
    </Section>
  );
}

/* The two most recent stories. */
function StoriesSection({ section }: SectionProps) {
  const { stories } = useContent();
  const all = linkSlot(section, 0);
  if (!stories.length) return null;
  return (
    <Section tone="ivory" ariaLabelledBy="stories-heading">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading id="stories-heading" eyebrow={section.eyebrow} title={section.title || "Recent reporting"} />
        {all ? <TextLink href={all.href}>{all.label}</TextLink> : null}
      </div>

      <RevealGroup as="ul" className="mt-11 grid max-w-5xl gap-7 md:grid-cols-2">
        {stories.slice(0, 2).map((story) => (
          <Reveal as="li" key={story.slug}>
            <StoryCard story={story} />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/* Charity Trek, the one dark section on the page. */
function CharityTrekSection({ section }: SectionProps) {
  return <CharityTrekBand section={section} />;
}

/*
  Masonry preview: photographs keep their own proportions instead of being
  cropped into identical boxes, which is what makes a contact sheet read as
  photography rather than as a grid of tiles.
*/
function GallerySection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const all = linkSlot(section, 0);
  const frames = (section.mediaKeys ?? []).map((key) => byKey(key)).filter((item): item is MediaItem => Boolean(item));
  if (!frames.length) return null;

  return (
    <Section ariaLabelledBy="gallery-heading">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading id="gallery-heading" eyebrow={section.eyebrow} title={section.title || "From programme days"} />
        {all ? <TextLink href={all.href}>{all.label}</TextLink> : null}
      </div>

      <RevealGroup as="ul" className="mt-11 columns-2 gap-5 lg:columns-4 [&>li]:mb-5 [&>li]:break-inside-avoid">
        {frames.map((media) => (
          <Reveal as="li" key={media.id}>
            <div className="overflow-hidden rounded-[16px] bg-mist shadow-[0_1px_2px_rgba(12,29,51,0.05),0_14px_30px_-24px_rgba(12,29,51,0.26)]">
              <MediaImage item={media} sizes="(min-width: 1024px) 31vw, 46vw" className="w-full" style={{ height: "auto" }} />
            </div>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/*
  Closing ask, with the accountability alongside it rather than buried.
  IRC and Mercy Corps both place registration status and the programme-spend
  figure next to the donate path. HISCUS has real registration numbers and
  no audited spend ratio yet, so the numbers it has are shown, and the ones
  it does not are named as pending rather than quietly omitted.
*/
function ClosingSection({ section }: SectionProps) {
  const settings = useSettings();
  const primary = linkSlot(section, 0);
  const secondary = linkSlot(section, 1);
  const accountability = visibleItems(section)[0];
  const note = section.body?.find((block) => block.kind === "paragraph")?.text;

  return (
    <Section tone="ivory" ariaLabelledBy="cta-heading">
      <div className="grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-20">
        <Reveal>
          <h2 id="cta-heading" style={{ fontSize: "var(--step-h2)" }}>
            {section.title || "Support a programme, contribute your time, or deliver alongside us."}
          </h2>
          {section.lead ? (
            <p className="mt-5 text-muted" style={{ fontSize: "var(--step-lead)" }}>
              {section.lead}
            </p>
          ) : null}
          {primary || secondary ? (
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {primary ? (
                <ButtonLink href={primary.href}>
                  {primary.label}
                  <ArrowRight />
                </ButtonLink>
              ) : null}
              {secondary ? (
                <ButtonLink href={secondary.href} variant="secondary">
                  {secondary.label}
                </ButtonLink>
              ) : null}
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-blue">
            {accountability?.title || "Accountability"}
          </h3>
          <dl className="mt-6 space-y-3.5">
            {settings.registrations.map((reg) => (
              <div
                key={reg.value}
                className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1 border-b border-line-strong/50 pb-3.5"
              >
                <dt className="text-[0.9375rem] text-muted">{reg.label}</dt>
                <dd className="font-serif text-[1.0625rem] text-midnight">{reg.value}</dd>
              </div>
            ))}
          </dl>
          {note ? <p className="mt-6 text-[0.875rem] leading-relaxed text-muted">{note}</p> : null}
          {accountability?.href && accountability.label ? (
            <div className="mt-5">
              <TextLink href={accountability.href}>{accountability.label}</TextLink>
            </div>
          ) : null}
        </Reveal>
      </div>
    </Section>
  );
}

export const homeSections: SectionRenderers = {
  "home.hero": HeroSection,
  "home.why-hiscus": WhySection,
  "home.evidence": EvidenceSection,
  "home.programmes": ProgrammesSection,
  "home.where-we-work": WhereWeWorkSection,
  "home.get-involved": GetInvolvedSection,
  "home.featured": FeaturedSection,
  "home.stories": StoriesSection,
  "home.charity-trek": CharityTrekSection,
  "home.gallery": GallerySection,
  "home.closing": ClosingSection,
};

export function HomePage() {
  return <PageSections renderers={homeSections} />;
}
